import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tractor, Loader2, Plus, Pencil, Trash2, History, Search, Users, UserX, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const heute = () => new Date().toISOString().slice(0, 10);

const LEER = () => ({
  liefernummer: "", name: "", hofname: "", adresse: "", plz: "", ort: "",
  milchsorte_id: "", gruppe_id: "", tour_id: "", aktiv: true,
  lat: null, lng: null,
  geschaetzte_tagesmenge_kg: null,
  steuermodell: "pauschaliert",
  custom_fields: {},
  gueltig_ab: heute(),
  aenderung_kommentar: "",
});

/**
 * Kleine Anzeige-Komponente: Liste zugeordneter Benutzerkonten.
 * - "Kein Zugang" wenn niemand zugeordnet ist
 * - Bei mehreren Benutzern: erster + Zähler
 */
const BenutzerCell = ({ benutzer, kompakt = false }) => {
  if (!benutzer || benutzer.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <UserX className="h-3 w-3" /> Kein Zugang
      </span>
    );
  }
  const primary = benutzer[0];
  const rest = benutzer.length - 1;
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs ${kompakt ? "" : ""}`}>
      <Users className="h-3 w-3 shrink-0 text-primary" />
      <span className="truncate">
        <span className={primary.aktiv ? "" : "line-through text-muted-foreground"}>
          {primary.name || primary.email}
        </span>
        {rest > 0 && (
          <span className="ml-1 text-muted-foreground">+{rest}</span>
        )}
      </span>
    </div>
  );
};

/**
 * Kompakte Jahresmengen-Anzeige mit Info über Datenquelle:
 * - "berechnet"  → Balken vollständig / 365 Tage Historie
 * - "hochgerechnet" → Balken teilweise / Basistage < 330
 * - "geschaetzt" → Balken leer / Wert aus Stammdaten
 */
const JahresmengeCell = ({ row }) => {
  const wert = row.effektive_jahresmenge_kg;
  const basis = row.jahresmenge_basis;
  if (!wert) {
    return <span className="text-xs text-muted-foreground">–</span>;
  }
  const label = new Intl.NumberFormat("de-AT").format(wert);
  const badgeMap = {
    berechnet: { text: "real", cls: "border-[hsl(var(--success))] text-[hsl(var(--success))]" },
    hochgerechnet: { text: "hochgerechnet", cls: "" },
    geschaetzt: { text: "Schätzung", cls: "" },
    unbekannt: { text: "?", cls: "" },
  };
  const b = badgeMap[basis] || badgeMap.unbekannt;
  return (
    <div className="flex flex-col leading-tight">
      <span className="font-num text-sm font-medium tabular-nums">{label} kg</span>
      <Badge variant="outline" className={`text-[10px] py-0 h-4 w-fit mt-0.5 ${b.cls}`}>
        {b.text}
        {basis === "hochgerechnet" && row.jahresmenge_basis_tage > 0 && (
          <span className="ml-1 text-muted-foreground font-num">({row.jahresmenge_basis_tage}T)</span>
        )}
      </Badge>
    </div>
  );
};

const HistorieDialog = ({ liefernummer, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/lieferanten/${liefernummer}/historie`)
      .then((res) => setRows(res.data)).finally(() => setLoading(false));
  }, [liefernummer]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Änderungshistorie {liefernummer}</DialogTitle>
          <DialogDescription>Alle Stammdatenversionen (SCD Type 2)</DialogDescription>
        </DialogHeader>
        {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gültig von</TableHead>
                  <TableHead>Gültig bis</TableHead>
                  <TableHead>Sorte / Gruppe / Tour</TableHead>
                  <TableHead>Bearbeiter</TableHead>
                  <TableHead>Kommentar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-num">{r.gueltig_ab}</TableCell>
                    <TableCell className="font-num">{r.gueltig_bis || "offen"}</TableCell>
                    <TableCell className="text-xs">
                      {r.name} · {r.ort}<br />
                      <span className="text-muted-foreground">
                        ms: {r.milchsorte_id?.slice(0, 8) || "–"} / gr: {r.gruppe_id?.slice(0, 8) || "–"} / t: {r.tour_id?.slice(0, 8) || "–"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{r.erstellt_von}</TableCell>
                    <TableCell className="text-xs italic max-w-[220px] truncate">{r.aenderung_kommentar}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminLieferantenPage() {
  const [rows, setRows] = useState([]);
  const [sorten, setSorten] = useState([]);
  const [gruppen, setGruppen] = useState([]);
  const [touren, setTouren] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);
  const [historie, setHistorie] = useState(null);
  const [suche, setSuche] = useState("");
  const [sucheDebounced, setSucheDebounced] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [bulkGeocoding, setBulkGeocoding] = useState(null);

  // Debounce (250 ms) analog Milchproben/Lieferantenseite
  useEffect(() => {
    const t = setTimeout(() => setSucheDebounced(suche.trim()), 250);
    return () => clearTimeout(t);
  }, [suche]);

  // Basisdaten (Sorten/Gruppen/Touren/Felder) einmalig
  useEffect(() => {
    Promise.all([
      api.get("/milchsorten"), api.get("/gruppen"),
      api.get("/touren"), api.get("/stammdaten/felder"),
    ]).then(([s, g, t, cf]) => {
      setSorten(s.data); setGruppen(g.data); setTouren(t.data);
      setCustomFields(cf.data.filter((f) => f.aktiv));
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = sucheDebounced ? { suche: sucheDebounced } : {};
    api.get("/lieferanten", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [sucheDebounced]);

  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    if (!form.gueltig_ab) {
      toast.error("Bitte Stichtag angeben.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        liefernummer: form.liefernummer,
        name: form.name, hofname: form.hofname,
        adresse: form.adresse, plz: form.plz, ort: form.ort,
        milchsorte_id: form.milchsorte_id || null,
        gruppe_id: form.gruppe_id || null,
        tour_id: form.tour_id || null,
        aktiv: form.aktiv,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        geschaetzte_tagesmenge_kg: form.geschaetzte_tagesmenge_kg
          ? Number(form.geschaetzte_tagesmenge_kg) : null,
        steuermodell: form.steuermodell || "pauschaliert",
        custom_fields: form.custom_fields || {},
        gueltig_ab: form.gueltig_ab,
        aenderung_kommentar: form.aenderung_kommentar || "",
      };
      if (form._isUpdate) {
        await api.put(`/lieferanten/${payload.liefernummer}`, payload);
        toast.success("Neue Version angelegt (Historie erweitert).");
      } else {
        await api.post("/lieferanten", payload);
        toast.success("Lieferant angelegt.");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  // Löst die Adresse eines Lieferanten via Nominatim in Koordinaten auf und
  // übernimmt das Ergebnis in das aktuelle Formular. Fehler → manuelle Eingabe.
  const geocodeFormAdresse = async () => {
    if (!form?.adresse && !form?.ort) {
      toast.error("Bitte Straße oder Ort eingeben.");
      return;
    }
    if (!form?.liefernummer) {
      toast.error("Bitte zuerst den Lieferant speichern, dann geocodieren.");
      return;
    }
    setGeocoding(true);
    try {
      const res = await api.post(`/lieferanten/${form.liefernummer}/geocode`);
      if (res.data?.ok) {
        setForm((f) => ({ ...f, lat: res.data.lat, lng: res.data.lng }));
        toast.success(
          `Adresse gefunden: ${res.data.display_name?.split(",").slice(0, 3).join(",")}${res.data.cached ? " (Cache)" : ""}`,
        );
      } else {
        toast.error(
          `Adresse „${res.data?.abfrage || ""}“ nicht gefunden. Bitte Koordinaten manuell eintragen.`,
        );
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setGeocoding(false); }
  };

  // Bulk-Geocoding: geocoded alle Lieferanten ohne Koordinaten via einzeln Requests.
  // Die Backend-Rate-Limitierung (~1.1 s/Req) drosselt automatisch.
  const bulkGeocode = async () => {
    if (!window.confirm(
      "Alle Lieferanten ohne Koordinaten via OpenStreetMap suchen? " +
      "Das kann bei vielen Betrieben mehrere Minuten dauern.",
    )) return;
    setBulkGeocoding({ running: true, total: 0, done: 0, ok: 0, fail: 0 });
    try {
      // Fetch all suppliers without coords
      const res = await api.get("/lieferanten");
      const kandidaten = (res.data || []).filter(
        (s) => s.aktiv && (s.lat == null || s.lng == null),
      );
      setBulkGeocoding({ running: true, total: kandidaten.length, done: 0, ok: 0, fail: 0 });
      if (kandidaten.length === 0) {
        toast.info("Alle aktiven Lieferanten haben bereits Koordinaten.");
        setBulkGeocoding(null);
        return;
      }
      let ok = 0, fail = 0;
      for (let i = 0; i < kandidaten.length; i++) {
        try {
          const r = await api.post(`/lieferanten/${kandidaten[i].liefernummer}/geocode`);
          if (r.data?.ok) ok += 1; else fail += 1;
        } catch { fail += 1; }
        setBulkGeocoding({ running: true, total: kandidaten.length, done: i + 1, ok, fail });
      }
      toast.success(`Bulk-Geocoding abgeschlossen: ${ok} erfolgreich, ${fail} fehlgeschlagen.`);
      setBulkGeocoding(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
      setBulkGeocoding(null);
    }
  };

  const loeschen = async () => {
    try {
      await api.delete(`/lieferanten/${del.liefernummer}`);
      toast.success("Lieferant deaktiviert (Historie bleibt erhalten).");
      setDel(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const oeffnenBearbeiten = (r) =>
    setForm({
      ...LEER(), ...r,
      custom_fields: r.custom_fields || {},
      _isUpdate: true, gueltig_ab: heute(), aenderung_kommentar: "",
    });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Lieferanten-Verwaltung</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length} {sucheDebounced ? "Treffer" : "aktive Lieferanten"} · Änderungen erzeugen eine neue Stammdaten-Version zum Stichtag
          </p>
        </div>
        <Button data-testid="supplier-create-button" onClick={() => setForm({ ...LEER() })}>
          <Plus className="h-4 w-4 mr-2" /> Neuer Lieferant
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] sm:flex-none">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="admin-suppliers-search"
            className="pl-8 sm:w-[320px] bg-card"
            placeholder="Name, Hof, Liefernr., Ort …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {bulkGeocoding && (
            <span className="text-xs text-muted-foreground font-num" data-testid="bulk-geocode-progress">
              Geocodierung: {bulkGeocoding.done} / {bulkGeocoding.total} · {bulkGeocoding.ok} ✓ · {bulkGeocoding.fail} ✗
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={bulkGeocode}
            disabled={!!bulkGeocoding}
            data-testid="bulk-geocode-button"
            title="Alle aktiven Lieferanten ohne Koordinaten via OpenStreetMap suchen. Läuft im Hintergrund mit 1 Sekunde Pause zwischen den Anfragen."
          >
            {bulkGeocoding ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <MapPin className="h-3.5 w-3.5 mr-1.5" />
            )}
            Alle geocoden
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rows.length ? (
            <>
              {/* Mobile: Karten-Liste (< md) */}
              <div className="md:hidden divide-y" data-testid="admin-suppliers-mobile">
                {rows.map((r) => (
                  <div key={r.liefernummer} className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-num font-medium">{r.liefernummer}</span>
                          <span className="font-medium truncate">{r.name}</span>
                          {r.aktiv ? (
                            <Badge variant="outline" className="text-[10px] py-0 h-4 text-[hsl(var(--success))] border-[hsl(var(--success))]">aktiv</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] py-0 h-4">inaktiv</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {r.hofname && <>{r.hofname} · </>}
                          {r.plz} {r.ort}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      <Badge variant="secondary" className="font-normal">{r.milchsorte}</Badge>
                      {r.gruppe && r.gruppe !== "-" && (
                        <Badge variant="outline" className="font-normal">{r.gruppe}</Badge>
                      )}
                      {r.tour && r.tour !== "-" && (
                        <Badge variant="outline" className="font-normal">Tour {r.tour}</Badge>
                      )}
                    </div>

                    {r.effektive_jahresmenge_kg > 0 && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-num font-medium tabular-nums">
                          {new Intl.NumberFormat("de-AT").format(r.effektive_jahresmenge_kg)} kg/Jahr
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] py-0 h-4 ${
                            r.jahresmenge_basis === "berechnet"
                              ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
                              : ""
                          }`}
                        >
                          {r.jahresmenge_basis === "berechnet" && "real"}
                          {r.jahresmenge_basis === "hochgerechnet" && `hochgerechnet · ${r.jahresmenge_basis_tage}T`}
                          {r.jahresmenge_basis === "geschaetzt" && "Schätzung"}
                        </Badge>
                      </div>
                    )}

                    <BenutzerCell benutzer={r.benutzer} />

                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[11px] text-muted-foreground font-num">
                        gültig ab {r.gueltig_ab}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          data-testid={`supplier-history-button-${r.liefernummer}`}
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => setHistorie(r.liefernummer)}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`supplier-edit-button-${r.liefernummer}`}
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => oeffnenBearbeiten(r)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`supplier-delete-button-${r.liefernummer}`}
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => setDel(r)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Tabelle (>= md) */}
              <div className="hidden md:block overflow-x-auto">
                <Table data-testid="admin-suppliers-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Liefernr.</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Hof / Ort</TableHead>
                      <TableHead>Sorte</TableHead>
                      <TableHead>Gruppe</TableHead>
                      <TableHead>Tour</TableHead>
                      <TableHead>Jahresmenge</TableHead>
                      <TableHead>Benutzer</TableHead>
                      <TableHead>Gültig ab</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.liefernummer}>
                        <TableCell className="font-num font-medium">{r.liefernummer}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.hofname}<br />{r.plz} {r.ort}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{r.milchsorte}</Badge></TableCell>
                        <TableCell className="text-sm">{r.gruppe}</TableCell>
                        <TableCell className="text-sm">{r.tour}</TableCell>
                        <TableCell data-testid={`supplier-jahresmenge-${r.liefernummer}`}>
                          <JahresmengeCell row={r} />
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <BenutzerCell benutzer={r.benutzer} />
                        </TableCell>
                        <TableCell className="font-num text-xs">{r.gueltig_ab}</TableCell>
                        <TableCell>{r.aktiv
                          ? <Badge variant="outline" className="text-[hsl(var(--success))] border-[hsl(var(--success))]">aktiv</Badge>
                          : <Badge variant="outline">inaktiv</Badge>}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon"
                              onClick={() => setHistorie(r.liefernummer)}
                              data-testid={`supplier-history-button-${r.liefernummer}`}>
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`supplier-edit-button-${r.liefernummer}`}
                              variant="ghost" size="icon"
                              onClick={() => oeffnenBearbeiten(r)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`supplier-delete-button-${r.liefernummer}`}
                              variant="ghost" size="icon"
                              onClick={() => setDel(r)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <EmptyState
              icon={Tractor}
              title={sucheDebounced ? "Keine Treffer." : "Noch keine Lieferanten angelegt."}
              text={sucheDebounced ? "Passen Sie den Suchbegriff an." : undefined}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="supplier-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form?._isUpdate ? "Lieferant bearbeiten" : "Neuer Lieferant"}</DialogTitle>
            <DialogDescription>
              Änderungen erzeugen eine neue historisierte Version mit dem angegebenen Stichtag.
              Alte Abrechnungen bleiben unverändert.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <Tabs defaultValue="stamm">
              <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="stamm" data-testid="supplier-tab-stamm" className="flex-1 sm:flex-none">Stammdaten</TabsTrigger>
                <TabsTrigger value="parameter" data-testid="supplier-tab-parameter" className="flex-1 sm:flex-none">Parameter</TabsTrigger>
                <TabsTrigger value="zuordnung" data-testid="supplier-tab-zuordnung" className="flex-1 sm:flex-none">
                  <span className="hidden sm:inline">Sorte / Gruppe / Tour</span>
                  <span className="sm:hidden">Zuordnung</span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="stamm" className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="space-y-1.5">
                  <Label>Liefernummer *</Label>
                  <Input data-testid="supplier-form-liefernummer" value={form.liefernummer}
                    onChange={(e) => setForm({ ...form, liefernummer: e.target.value })}
                    disabled={form._isUpdate} />
                </div>
                <div className="space-y-1.5">
                  <Label>Name *</Label>
                  <Input data-testid="supplier-form-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-1.5"><Label>Hofname</Label>
                  <Input value={form.hofname} onChange={(e) => setForm({ ...form, hofname: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Adresse</Label>
                  <Input data-testid="supplier-form-adresse" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>PLZ</Label>
                  <Input value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Ort</Label>
                  <Input value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} /></div>
                <div className="sm:col-span-2 flex items-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={geocodeFormAdresse}
                    disabled={geocoding || !form._isUpdate || (!form.adresse && !form.ort)}
                    data-testid="supplier-geocode-button"
                    title={
                      !form._isUpdate
                        ? "Bitte den Lieferant zuerst speichern, dann kann die Adresse geocoded werden"
                        : "Adresse via OpenStreetMap in Koordinaten umwandeln"
                    }
                  >
                    {geocoding ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1.5" />}
                    Koordinaten aus Adresse ermitteln
                  </Button>
                  <p className="text-[11px] text-muted-foreground">
                    Wird die Adresse nicht gefunden (kommt im ländlichen Raum vor), Lat/Lng bitte manuell eintragen.
                  </p>
                </div>
                <div className="space-y-1.5"><Label>Breitengrad (Lat)</Label>
                  <Input type="number" step="0.000001" value={form.lat ?? ""} onChange={(e) => setForm({ ...form, lat: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Längengrad (Lng)</Label>
                  <Input type="number" step="0.000001" value={form.lng ?? ""} onChange={(e) => setForm({ ...form, lng: e.target.value })} /></div>
                <div className="flex items-center gap-2 pt-6 sm:col-span-2">
                  <Switch checked={form.aktiv} onCheckedChange={(v) => setForm({ ...form, aktiv: v })} />
                  <Label>Aktiv</Label>
                </div>
              </TabsContent>
              <TabsContent value="parameter" className="space-y-3 pt-2">
                <p className="text-xs text-muted-foreground">
                  Zusatzparameter (Kontrollstellennummer, UID, IBAN, …) – die mit Andruck-Flag versehenen Felder erscheinen auf der Abrechnung.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {customFields.map((f) => (
                    <div key={f.id} className="space-y-1.5">
                      <Label className="flex items-center gap-2">
                        {f.label}
                        {f.andruck_auf_abrechnung && <Badge variant="outline" className="text-[10px] py-0 h-4">Abrechnung</Badge>}
                      </Label>
                      {f.typ === "boolean" ? (
                        <Switch checked={!!form.custom_fields?.[f.key]} onCheckedChange={(v) =>
                          setForm({ ...form, custom_fields: { ...form.custom_fields, [f.key]: v } })} />
                      ) : (
                        <Input
                          type={f.typ === "number" ? "number" : (f.typ === "date" ? "date" : "text")}
                          data-testid={`supplier-custom-${f.key}`}
                          value={form.custom_fields?.[f.key] ?? ""}
                          onChange={(e) => setForm({
                            ...form, custom_fields: { ...form.custom_fields, [f.key]: e.target.value },
                          })}
                        />
                      )}
                    </div>
                  ))}
                  {customFields.length === 0 && (
                    <p className="col-span-2 text-sm text-muted-foreground">
                      Noch keine Parameter definiert. Verwaltung unter Einstellungen → Stammdaten-Parameter.
                    </p>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="zuordnung" className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="space-y-1.5">
                  <Label>Milchsorte</Label>
                  <Select value={form.milchsorte_id || ""} onValueChange={(v) => setForm({ ...form, milchsorte_id: v })}>
                    <SelectTrigger data-testid="supplier-form-milktype"><SelectValue placeholder="Wählen …" /></SelectTrigger>
                    <SelectContent>{sorten.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Gruppe</Label>
                  <Select value={form.gruppe_id || ""} onValueChange={(v) => setForm({ ...form, gruppe_id: v })}>
                    <SelectTrigger data-testid="supplier-form-group"><SelectValue placeholder="Wählen …" /></SelectTrigger>
                    <SelectContent>{gruppen.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tour</Label>
                  <Select value={form.tour_id || ""} onValueChange={(v) => setForm({ ...form, tour_id: v })}>
                    <SelectTrigger data-testid="supplier-form-tour"><SelectValue placeholder="Wählen …" /></SelectTrigger>
                    <SelectContent>{touren.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} · {t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Steuermodell</Label>
                  <Select
                    value={form.steuermodell || "pauschaliert"}
                    onValueChange={(v) => setForm({ ...form, steuermodell: v })}
                  >
                    <SelectTrigger data-testid="supplier-form-steuermodell">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pauschaliert">Pauschaliert</SelectItem>
                      <SelectItem value="regelbesteuert">Regelbesteuert</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Legt fest, welcher USt-Satz aus dem Preismodell bei Abrechnungen verwendet wird.
                  </p>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Produktionsmenge</Label>
                  <div className="rounded-lg border p-3 space-y-3 bg-secondary/30">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Ø Tagesmenge</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="1" min="0"
                            inputMode="numeric"
                            data-testid="supplier-form-tagesmenge"
                            value={form.geschaetzte_tagesmenge_kg ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const val = raw === "" ? null : Number(raw);
                              setForm({
                                ...form,
                                geschaetzte_tagesmenge_kg: val,
                                // Bidirektional: Jahresmenge sofort mitziehen
                                geschaetzte_jahresmenge_kg: val === null ? null : Math.round(val * 365),
                              });
                            }}
                            placeholder="z. B. 450"
                            className="max-w-[140px] font-num"
                          />
                          <span className="text-sm text-muted-foreground shrink-0">kg / Tag</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Ø Jahresmenge</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="1000" min="0"
                            inputMode="numeric"
                            data-testid="supplier-form-jahresmenge"
                            value={form.geschaetzte_jahresmenge_kg ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const val = raw === "" ? null : Number(raw);
                              setForm({
                                ...form,
                                geschaetzte_jahresmenge_kg: val,
                                // Bidirektional: Tagesmenge sofort mitziehen
                                geschaetzte_tagesmenge_kg: val === null ? null : Math.round((val / 365) * 10) / 10,
                              });
                            }}
                            placeholder="z. B. 164 250"
                            className="max-w-[180px] font-num"
                          />
                          <span className="text-sm text-muted-foreground shrink-0">kg / Jahr</span>
                        </div>
                      </div>
                    </div>

                    {/* Info-Zeile: berechnete Werte falls Historie vorhanden */}
                    {(form.berechnete_tagesmenge_kg != null || form.berechnete_jahresmenge_kg != null) && (
                      <div
                        className="flex flex-wrap items-center gap-2 pt-2 border-t"
                        data-testid="supplier-berechnet-info"
                      >
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          Auswertung real:
                        </span>
                        {form.berechnete_tagesmenge_kg != null && (
                          <Badge variant="secondary" className="font-num">
                            Ø {form.berechnete_tagesmenge_kg} kg/Tag
                            <span className="text-muted-foreground ml-1">({form.tagesmenge_basis_tage} T)</span>
                          </Badge>
                        )}
                        {form.berechnete_jahresmenge_kg != null && (
                          <Badge variant="secondary" className="font-num">
                            Σ {new Intl.NumberFormat("de-AT").format(form.berechnete_jahresmenge_kg)} kg/Jahr
                            <span className="text-muted-foreground ml-1">
                              ({form.jahresmenge_basis === "berechnet" ? "vollständig" : `hochgerechnet aus ${form.jahresmenge_basis_tage} T`})
                            </span>
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Grundlage für die Tourenplanung. Beide Felder aktualisieren sich automatisch beim Tippen
                    (Ø-Jahr = 365 &times; Ø-Tag). Sobald ein volles Jahr echte Lieferdaten vorliegt, wird der
                    tatsächlich errechnete Wert bevorzugt &ndash; erst dann sind saisonale Schwankungen abgedeckt.
                    Die Erwartungsmenge pro Abholung berücksichtigt zusätzlich den Abholzyklus der zugeordneten Tour.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Stichtag (gültig ab) *</Label>
                <Input type="date" data-testid="supplier-form-gueltig-ab" value={form?.gueltig_ab || ""}
                  onChange={(e) => setForm({ ...form, gueltig_ab: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Kommentar zur Änderung</Label>
                <Input data-testid="supplier-form-kommentar" value={form?.aenderung_kommentar || ""}
                  onChange={(e) => setForm({ ...form, aenderung_kommentar: e.target.value })}
                  placeholder="z. B. Wechsel auf Heumilch" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
            <Button data-testid="supplier-form-save-button" onClick={speichern}
              disabled={saving || !form?.liefernummer || !form?.name || !form?.gueltig_ab}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {historie && <HistorieDialog liefernummer={historie} onClose={() => setHistorie(null)} />}

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lieferant deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {del?.name} (Liefernr. {del?.liefernummer}) wird deaktiviert – die Historie und alle Abrechnungen bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="supplier-delete-confirm-button" onClick={loeschen}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Deaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
